import { observer } from 'mobx-react-lite';
import { motion, AnimatePresence } from 'framer-motion';
import { CartItemList } from '../CartItemList';
import {
  ICartItem,
  useCartContext,
} from 'screens/AccessExpressV3/CartProvider';
import { useMemo, useState } from 'react';
import { CartOutline, ChevronDown } from 'react-ionicons';
import Tag from 'components/Tag';
import clsx from 'clsx';

const dropdownVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    marginTop: 0,
  },
  visible: {
    opacity: 1,
    height: 'auto',
    marginTop: '0.5rem',
    transition: {
      height: {
        duration: 0.3,
        ease: 'easeInOut',
      },
      opacity: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  },
  exit: {
    opacity: 0,
    height: 0,
    marginTop: 0,
    transition: {
      height: {
        duration: 0.3,
        ease: 'easeInOut',
      },
      opacity: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  },
};

const BasketDropdown = observer(
  ({ locationId }: { locationId?: string | number | undefined }) => {
    const cart = useCartContext();
    const [isOpen, setIsOpen] = useState(false);

    const handleDelete = async (item: ICartItem) => {
      cart.setLoadingDraftQuote(true);
      if (item.category === 'Access') {
        if (item.subtype === 'starlink') {
          await cart.removeStarlinkCartItem(
            item.item_details!,
            locationId as string,
          );
        } else
          await cart.removeAccessCartItem(
            item.item_details?.offering_id ?? '',
            locationId as string,
          );
      } else if (item.category === 'Mobility') {
        await cart.removeMobilityCartItem(String(item?.id));
      }
      cart.setLoadingDraftQuote(false);
    };

    const handleQuantityChange = (item: ICartItem, newQty: number) => {
      if (item.category === 'Access' && item.subtype === 'starlink') {
        cart.updateStarlinkItemQuantity(item, newQty);
        return;
      }
      cart.updateItemQuantity(item.id, newQty);
    };

    const items = useMemo(() => {
      if (cart.items) {
        if (typeof locationId === 'string') {
          return cart.itemsByLocation(locationId);
        }
      }
      return [];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId, cart, cart.items]);

    const categories = useMemo(() => {
      return items.reduce<string[]>((acc, item) => {
        if (!acc.includes(item.category)) {
          acc.push(item.category);
        }
        return acc;
      }, []);
    }, [items]);

    const totalQuantityForLocation = useMemo(
      () => items.reduce((sum, item) => sum + item.quantity, 0),
      [items],
    );

    return (
      <div
        className={clsx('group rounded', {
          ' hover:bg-background-base-surface-2  hover:text-content-base-default':
            !isOpen,
          'bg-background-base-surface-2  text-content-base-default': isOpen,
        })}
      >
        <div
          onClick={() => setIsOpen(!isOpen)}
          className={clsx('flex cursor-pointer items-center justify-between', {
            'py-1 group-hover:p-2': !isOpen,
            'p-2': isOpen,
          })}
        >
          <div
            className={clsx({
              'flex gap-4 group-hover:hidden': !isOpen,
              hidden: isOpen,
            })}
          >
            {categories.map((category) => (
              <Tag
                key={category}
                content={{ type: 'label', label: category }}
                status="custom"
                className={clsx('bg-opacity-[8%]', {
                  'bg-[#9FF4A9] text-[#9FF4A9]': category === 'Access',
                  'bg-[#6C9DFF] text-[#6C9DFF]': category === 'Mobility',
                  'bg-[#8989FF] text-[#8989FF]': category === 'Voice',
                  'bg-[#78CFDA] text-[#78CFDA]': category === 'MNS',
                })}
              />
            ))}
          </div>
          <div
            className={clsx(
              'items-center justify-center gap-1 text-xs font-semibold text-content-base-default ',
              { 'hidden group-hover:flex': !isOpen, flex: isOpen },
            )}
          >
            Cart{' '}
            <span className={clsx({ '': !isOpen, 'rotate-180': isOpen })}>
              <ChevronDown width="16px" height="16px" color="inherit" />
            </span>
          </div>
          <div
            className={clsx('flex gap-0.5 text-xs font-semibold', {
              'text-content-base-subdued group-hover:text-content-base-default':
                !isOpen,
              'text-content-base-default': isOpen,
            })}
          >
            <CartOutline width="16px" height="16px" color="inherit" />
            <span>({totalQuantityForLocation})</span>
          </div>
        </div>
        <AnimatePresence>
          {isOpen && items.length > 0 && (
            <motion.div
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={dropdownVariants}
              className="overflow-hidden px-2 pb-2"
            >
              <CartItemList
                items={items}
                className="!gap-2"
                // Edit functionality is temporarily disabled and will be re-enabled with G360-3189
                // onEdit={handleEdit}
                onDelete={handleDelete}
                onQuantityChange={handleQuantityChange}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

export default BasketDropdown;
